import React from "react";
import { Box, Typography } from "@mui/material";
import { Check } from "@mui/icons-material";
import { ACCENT } from "../BusinessOnboard.constant";

interface IOnboardStepperProps {
  /** Zero-based index of the active step. */
  activeStep: number;
  labels: string[];
}

/**
 * Numbered step indicator matching the mockups:
 * completed = filled red with check, active = white with red ring + red number,
 * upcoming = grey outline. Connector lines turn red once passed.
 */
export const OnboardStepper: React.FC<IOnboardStepperProps> = ({
  activeStep,
  labels,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      {labels.map((label, index) => {
        const isCompleted = index < activeStep;
        const isActive = index === activeStep;
        const isLast = index === labels.length - 1;

        return (
          <React.Fragment key={label}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  boxSizing: "border-box",
                  transition: "all 0.2s ease",
                  ...(isCompleted
                    ? {
                        backgroundColor: ACCENT.main,
                        color: "#fff",
                        border: `2px solid ${ACCENT.main}`,
                      }
                    : isActive
                      ? {
                          backgroundColor: "#fff",
                          color: ACCENT.main,
                          border: `2px solid ${ACCENT.ring}`,
                        }
                      : {
                          backgroundColor: "#fff",
                          color: "text.disabled",
                          border: "2px solid",
                          borderColor: "grey.300",
                        }),
                }}
              >
                {isCompleted ? (
                  <Check sx={{ fontSize: 18 }} />
                ) : (
                  index + 1
                )}
              </Box>
              <Typography
                sx={{
                  mt: 0.75,
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  color: isActive || isCompleted ? "text.primary" : "text.disabled",
                }}
              >
                {label}
              </Typography>
            </Box>

            {!isLast && (
              <Box
                sx={{
                  flex: 1,
                  height: "2px",
                  mt: "15px",
                  mx: 0.5,
                  backgroundColor: index < activeStep ? ACCENT.main : "grey.300",
                  transition: "background-color 0.2s ease",
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};
